/**
 * A Data frame's table (Part 05), addressed by the Data frame's id: its
 * description, pages of rows, batched writes, Undo last change, field
 * changes (with their preview) and a chart's points for a picked range. Errors render inline, so every request is
 * silent and rejects with a `DatasetError` carrying the server's code.
 */
import { apiRequest, type ApiError } from "./client";

interface ApiResponse<T> {
  status: "success";
  data: T;
}

export type DatasetFieldType =
  | "text"
  | "number"
  | "decimal"
  | "boolean"
  | "date"
  | "datetime"
  | "select";

export interface DatasetOption {
  id: string;
  label: string;
}

export interface DatasetField {
  id: string;
  name: string;
  type: DatasetFieldType;
  required: boolean;
  default: DatasetValue;
  options?: DatasetOption[];
  scale?: number;
  /** What the field is for; an empty cell shows it as a placeholder. */
  description?: string;
}

/** A cell: decimals are strings ("12.50"), selects their option id. */
export type DatasetValue = string | number | boolean | null;

export interface DatasetRow {
  id: string;
  revision: number;
  position: number;
  /** Keyed by field id. */
  values: Record<string, DatasetValue>;
  updated_by_kind: "human" | "agent";
  updated_at: string;
}

export interface DatasetConsumer {
  frame_id: string;
  title: string;
}

export interface DatasetDescription {
  frame_id: string;
  title: string;
  fields: DatasetField[];
  schema_revision: number;
  data_revision: number;
  row_count: number;
  /** The charts reading this table. */
  consumers: DatasetConsumer[];
}

/**
 * One entry of a full field list. With an `id` it keeps that field (rename,
 * options); without one it is a new field. An id left out deletes the field.
 * Options are `{id, label}` to keep one, a label for a new one.
 */
export interface DatasetFieldSpec {
  id?: string;
  name: string;
  type: DatasetFieldType;
  required?: boolean;
  default?: DatasetValue;
  options?: Array<DatasetOption | string>;
  scale?: number;
}

/** What a field change deletes, from the schema preview. */
export interface DatasetSchemaImpact {
  destructive: boolean;
  deleted_fields: Array<{ id: string; name: string }>;
  deleted_options: Array<{ field_id: string; id: string; label: string }>;
  /** Rows that lose a value. */
  affected_rows: number;
  /** The charts reading this table. */
  consumers: DatasetConsumer[];
}

export interface DatasetSchemaChange {
  fields: DatasetFieldSpec[];
  expected_schema_revision: number;
  /** A person's confirmation of a change that deletes data. */
  confirm_destructive?: true;
}

export interface DatasetRowsQuery {
  limit?: number;
  offset?: number;
}

export interface DatasetRowsPage {
  rows: DatasetRow[];
  total: number;
  data_revision: number;
  schema_revision: number;
}

export type DatasetOp =
  | { op: "insert"; id?: string; values: Record<string, DatasetValue> }
  | {
      op: "update";
      id: string;
      expected_revision: number;
      values: Record<string, DatasetValue>;
    }
  | { op: "delete"; id: string; expected_revision: number };

export interface DatasetWriteResult {
  data_revision: number;
  /** The inserted and updated rows, in op order. */
  rows: DatasetRow[];
  deleted: string[];
}

/** A chart's period: everything, the last 7, 30 or 90 days, or two dates. */
export type ChartRange = "all" | "7d" | "30d" | "90d" | ChartDateSpan;

export interface ChartDateSpan {
  /** "YYYY-MM-DD" */
  from: string;
  to: string;
}

/** A chart source's fields (ids or names) and the range to compute. */
export interface ChartPointsRequest {
  label_field: string;
  value_field?: string | null;
  aggregate: string;
  limit?: number;
  date_field?: string;
  range?: ChartRange;
}

export interface ChartPointsResult {
  source: Record<string, unknown>;
  points: Array<{ label: string; value: number }>;
  data_revision: number;
}

export type DatasetErrorCode =
  | "invalid"
  | "conflict"
  | "schema_conflict"
  | "confirm_required"
  | "too_large"
  | "table_full"
  | "not_found"
  | "forbidden"
  /** No code from the server: a network failure or an unexpected error. */
  | "failed";

const CODES: ReadonlySet<string> = new Set<DatasetErrorCode>([
  "invalid",
  "conflict",
  "schema_conflict",
  "confirm_required",
  "too_large",
  "table_full",
  "not_found",
  "forbidden",
]);

/** A refused table request, with what the UI needs to answer it. */
export class DatasetError extends Error {
  readonly code: DatasetErrorCode;
  /** 0 when no response arrived. */
  readonly status: number;
  /** A conflict's current row; `null` when the row no longer exists. */
  readonly row?: DatasetRow | null;
  /** The op index a conflict or an invalid value belongs to. */
  readonly op?: number;
  /** The field an invalid value belongs to. */
  readonly fieldId?: string;

  constructor(error: unknown) {
    const api = isApiError(error) ? error : null;
    const data = api?.data ?? {};
    super(typeof data.error === "string" ? data.error : "Request failed");
    this.name = "DatasetError";
    this.status = api?.status ?? 0;
    this.code =
      typeof data.code === "string" && CODES.has(data.code)
        ? (data.code as DatasetErrorCode)
        : this.status === 404
          ? "not_found"
          : "failed";
    if ("row" in data) this.row = (data.row as DatasetRow | null) ?? null;
    if (typeof data.op === "number") this.op = data.op;
    if (typeof data.field_id === "string") this.fieldId = data.field_id;
  }
}

function isApiError(error: unknown): error is ApiError {
  return (
    typeof error === "object" &&
    error !== null &&
    typeof (error as ApiError).status === "number" &&
    typeof (error as ApiError).data === "object"
  );
}

const tablePath = (projectId: string, frameId: string) =>
  `/api/v1/projects/${encodeURIComponent(projectId)}/datasets/${encodeURIComponent(frameId)}/`;

async function request<T>(
  url: string,
  body?: unknown,
  method: "POST" | "PATCH" | undefined = body === undefined
    ? undefined
    : "POST",
): Promise<T> {
  try {
    const response = await apiRequest<ApiResponse<T>>(url, {
      ...(method ? { method } : {}),
      ...(body === undefined ? {} : { body }),
      silent: true,
    });
    return response.data;
  } catch (error) {
    throw new DatasetError(error);
  }
}

export function getDataset(
  projectId: string,
  frameId: string,
): Promise<DatasetDescription> {
  return request(tablePath(projectId, frameId));
}

export function queryRows(
  projectId: string,
  frameId: string,
  query: DatasetRowsQuery,
): Promise<DatasetRowsPage> {
  return request(`${tablePath(projectId, frameId)}query/`, query);
}

export function writeRows(
  projectId: string,
  frameId: string,
  ops: DatasetOp[],
): Promise<DatasetWriteResult> {
  return request(`${tablePath(projectId, frameId)}rows/batch/`, { ops });
}

/**
 * Undo last change: a person's action only. `expectedDataRevision` is the
 * table revision the person sees; a newer write since, or a touched row that
 * changed, refuses it with a `conflict`.
 */
export function undoLastChange(
  projectId: string,
  frameId: string,
  expectedDataRevision: number,
): Promise<{ data_revision: number }> {
  return request(`${tablePath(projectId, frameId)}undo/`, {
    expected_data_revision: expectedDataRevision,
  });
}

export function previewSchemaChange(
  projectId: string,
  frameId: string,
  fields: DatasetFieldSpec[],
): Promise<DatasetSchemaImpact> {
  return request(`${tablePath(projectId, frameId)}schema/preview/`, {
    fields,
  });
}

/** A change that deletes data needs `confirm_destructive`, a person's only. */
export function changeSchema(
  projectId: string,
  frameId: string,
  change: DatasetSchemaChange,
): Promise<DatasetDescription> {
  return request(`${tablePath(projectId, frameId)}schema/`, change, "PATCH");
}

export function fetchChartPoints(
  projectId: string,
  tableFrameId: string,
  body: ChartPointsRequest,
): Promise<ChartPointsResult> {
  return request(`${tablePath(projectId, tableFrameId)}chart-points/`, body);
}
